import type { Project, ProjectInput } from './project.model';
import type { ProjectPayload, ProjectResponse } from './projects.contract';

export const PROJECT_NAME_MAX_RUNES = 200;

const API_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DISPLAY_DATE = /^(\d{2})\.(\d{2})\.(\d{4})$/;

const str = (value: string | null | undefined): string => (value ?? '').trim();

/** `yyyy-MM-dd` → `dd.MM.yyyy`. Empty in, empty out. */
export function civilToDisplay(value: string | null | undefined): string {
  const trimmed = str(value);

  if (!trimmed) {
    return '';
  }

  const match = API_DATE.exec(trimmed);

  return match ? `${match[3]}.${match[2]}.${match[1]}` : trimmed;
}

/** `dd.MM.yyyy` (or already `yyyy-MM-dd`) → `yyyy-MM-dd`. Empty in, empty out. */
export function civilToApi(value: string | null | undefined): string {
  const trimmed = str(value);

  if (!trimmed) {
    return '';
  }

  if (API_DATE.test(trimmed)) {
    return trimmed;
  }

  const match = DISPLAY_DATE.exec(trimmed);

  return match ? `${match[3]}-${match[2]}-${match[1]}` : '';
}

export function toProject(response: ProjectResponse): Project {
  const editedAt = str(response.edited_on_date);

  return {
    id: str(response.id),
    name: str(response.project_name),
    colorPalette: str(response.color_palette),
    start: civilToDisplay(response.project_start),
    end: civilToDisplay(response.project_end),
    createdBy: str(response.created_by_full_name),
    createdAt: str(response.created_on_date),
    editedBy: str(response.edited_by_full_name),
    editedAt: editedAt === '' ? null : editedAt,
  };
}

export function toProjects(
  data: readonly ProjectResponse[] | null | undefined,
): readonly Project[] {
  return (data ?? []).map(toProject);
}

export function toProjectPayload(input: ProjectInput): ProjectPayload {
  return {
    project_name: str(input.name),
    color_palette: str(input.colorPalette),
    project_start: civilToApi(input.start),
    project_end: civilToApi(input.end),
  };
}
